import { COLORS } from "../hooks/useAudioRecorder";
import PageShell from "../components/PageShell";
import AuthChip from "../components/AuthChip";
import BackLink from "../components/BackLink";
import AppFooter from "../components/AppFooter";
import { JsonLd } from "../hooks/usePageMeta";
import { webPageLd } from "../seo";

const LAST_UPDATED = "2026-05-29";
const CONTACT_EMAIL = "hello@handinvoice.com";

export default function Privacy() {
  return (
    <PageShell testId="privacy-page" padding="20px 18px 28px">
      {/* WebPage structured data. */}
      <JsonLd data={webPageLd("/privacy")} />

      <header
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 18,
        }}
      >
        <BackLink to="/" label="Home" testId="privacy-back-link" />
        <AuthChip />
      </header>

      <h1
        style={{
          fontFamily: "'Instrument Serif', Georgia, serif",
          fontStyle: "italic",
          fontSize: 36,
          margin: "0 0 8px 0",
        }}
      >
        Privacy<span style={{ color: COLORS.yellow }}>.</span>
      </h1>
      <p
        style={{
          fontFamily: "'JetBrains Mono', ui-monospace, monospace",
          fontSize: 11,
          color: COLORS.faint,
          letterSpacing: "0.04em",
          margin: "0 0 28px 0",
        }}
      >
        // plain language, no marketing fluff
      </p>

      <Section title="What we collect — when you're not signed in">
        <Bullet>
          <strong>Nothing on our servers.</strong> If you use the app without
          signing in, we don't save anything about you on our end.
        </Bullet>
        <Bullet>
          <strong>Your business details, only if you enter them.</strong> They
          stay inside your web browser on this device (just for the current tab)
          — close the tab and they're gone. They never reach us.
        </Bullet>
        <Bullet>
          <strong>A couple of random ID numbers in your browser.</strong> One is
          a device tag we keep in case we ever need it to prevent spam (today
          it isn't linked to anything about you); the other simply stops our
          usage counter from counting the same visit twice. Neither one
          identifies you personally.
        </Bullet>
        <Bullet>
          <strong>Anonymous usage stats.</strong> We use an analytics tool
          (called PostHog) to see things like which pages and buttons get used,
          so we can improve the app. This goes to PostHog's own servers in the
          United States, not ours. It never records your screen, and none of it
          is ever sold or given to advertisers.
        </Bullet>
      </Section>

      <Section title="What we collect — when you sign in">
        <Bullet>
          <strong>Your business details</strong> (name, contact info, license
          number, payment terms, tax settings, and your logo if you add one) —
          so they appear on your invoices. Saved to your account, not just this
          device.
        </Bullet>
        <Bullet>
          <strong>Your invoices</strong> — customer names, amounts, and the text
          we transcribed from your voice — plus a copy of your business details
          as they were at the time. This is what lets us show your history and
          re-create the PDF when you download it again.
        </Bullet>
        <Bullet>
          <strong>Your email address.</strong> Signing in is handled by a
          specialist login service (called Clerk), so we never see or store your
          password.
        </Bullet>
        <Bullet>
          <strong>A short activity log</strong> — which invoices you made, when,
          and the total amount — so we can understand how the app is used. It
          never includes customer names or the line items.
        </Bullet>
      </Section>

      <Section title="What we don't keep">
        <Bullet>
          <strong>Voice recordings.</strong> Your recording is held only while
          you're recording, and is permanently deleted the moment you finalize
          the invoice. It never gets saved to our database.
        </Bullet>
        <Bullet>
          <strong>The finished PDF file (for now).</strong> We save your
          invoice's information, not the PDF itself — the PDF is rebuilt from
          that saved information each time you download it. The numbers (amounts,
          customer, line items) are locked once you finalize; only the layout
          reflects the current version of the app, so a later download may look
          slightly different even though the figures are identical. (Saving the
          exact original file is on our list.)
        </Bullet>
        <Bullet>
          <strong>Screen recordings.</strong> We never record or replay what's
          on your screen, so the customer names and amounts on your invoices are
          never captured.
        </Bullet>
        <Bullet>
          <strong>Your IP address, in our app's own records.</strong> (The
          companies that host our servers and handle sign-in may note it briefly
          for normal security and to keep the service running.)
        </Bullet>
        <Bullet>
          <strong>Your data, sold or shared for ads/marketing.</strong> Ever.
        </Bullet>
      </Section>

      <Section title="Who can see your data">
        <Bullet>
          <strong>You.</strong> Your invoices are private to your account —
          other users can't see them.
        </Bullet>
        <Bullet>
          <strong>Us.</strong> The small team building HandInvoice can access
          stored data to provide support and keep things running. That's normal
          for any online service — we'd rather just say so.
        </Bullet>
        <Bullet>
          <strong>A few trusted services we're built on.</strong> Like most
          apps, HandInvoice relies on outside services for specific jobs:
        </Bullet>
        <Bullet>
          <strong>OpenAI</strong> — the AI service that turns your voice into
          text. Your audio passes through it briefly to be transcribed, then
          isn't kept.
        </Bullet>
        <Bullet>
          <strong>Our database provider (MongoDB)</strong> — where your invoices
          and business details are stored once you're signed in. Nothing is
          stored there if you're not signed in.
        </Bullet>
        <Bullet>
          <strong>Our sign-in service (Clerk)</strong> — manages logging in
          (email and password, or "Continue with Google") so your password
          never touches our app.
        </Bullet>
        <Bullet>
          <strong>Our analytics tool (PostHog)</strong> — only sees anonymous
          things like page views and clicks, never your invoice details. The
          data is stored on PostHog's servers in the United States.
        </Bullet>
      </Section>

      <Section title="Your rights">
        <Bullet>
          <strong>Delete any invoice</strong> anytime from your Invoices page.
        </Bullet>
        <Bullet>
          <strong>Deleted invoices stay in your trash for 30 days</strong>,
          then are permanently removed. You can restore them anytime within
          that window from the invoices page.
        </Bullet>
        <Bullet>
          <strong>Delete your account</strong> — email us at{" "}
          <code style={codeStyle}>{CONTACT_EMAIL}</code> and we'll wipe your
          data. (Self-serve account deletion is on the roadmap.)
        </Bullet>
        <Bullet>
          <strong>Export your data</strong> — coming soon. Email us in the
          meantime.
        </Bullet>
      </Section>

      <Section title="Contact">
        <Bullet>
          Questions, concerns, deletion requests:{" "}
          <code style={codeStyle}>{CONTACT_EMAIL}</code>
        </Bullet>
      </Section>

      <p
        style={{
          fontFamily: "'JetBrains Mono', ui-monospace, monospace",
          fontSize: 10,
          color: COLORS.fainter,
          letterSpacing: "0.06em",
          marginTop: 32,
          marginBottom: 14,
        }}
      >
        // last updated: {LAST_UPDATED}
      </p>

      <AppFooter />
    </PageShell>
  );
}

const codeStyle = {
  fontFamily: "'JetBrains Mono', ui-monospace, monospace",
  fontSize: 14,
  color: COLORS.yellow,
  background: "rgba(232, 220, 45, 0.08)",
  padding: "1px 6px",
  borderRadius: 4,
};

const Section = ({ title, children }) => (
  <section style={{ marginBottom: 24 }}>
    <h2
      style={{
        // Type treatment matched to the FAQ question rows (pages/Faq.jsx)
        // so the two pages read as siblings — same mono heading line over a
        // dashed rule. Privacy stays static, FAQ expands. Keep these in sync.
        fontFamily: "'JetBrains Mono', ui-monospace, monospace",
        fontSize: 13,
        letterSpacing: "0.04em",
        lineHeight: 1.5,
        color: COLORS.text,
        margin: "0 0 14px 0",
        paddingBottom: 12,
        borderBottom: `1px dashed ${COLORS.rule}`,
      }}
    >
      {title}
    </h2>
    <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>{children}</ul>
  </section>
);

const Bullet = ({ children }) => (
  <li
    style={{
      fontFamily: "'Inter', system-ui, sans-serif",
      fontSize: 14,
      lineHeight: 1.6,
      color: COLORS.text,
      paddingLeft: 18,
      marginBottom: 10,
      position: "relative",
    }}
  >
    <span
      aria-hidden
      style={{
        position: "absolute",
        left: 0,
        top: 0,
        color: COLORS.yellow,
        fontFamily: "'JetBrains Mono', ui-monospace, monospace",
      }}
    >
      ›
    </span>
    {children}
  </li>
);
